import { centsToDollars, dollarsToCents } from '@fruitful/fruitils';
import {
    type BillActivationStatus,
    BillCategory,
    BillingCadence,
} from '@fruitful/types';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import React, { useEffect, useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Label,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { useUpsertBillAndRefetch } from '../../../bills/useUpsertBill';
import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import type { Bill } from '../../entityTypes';

export const BillEditModal = ({
    bill,
    isOpen,
    onClose,
}: {
    bill: Bill;
    isOpen: boolean;
    onClose: () => void;
}) => {
    const resetForm = () => {
        setName(bill.displayName ?? bill.name ?? bill.payee ?? '');
        setAmount(centsToDollars(bill.amount ?? 0) ?? 0);
        setCategory(bill.category ?? '');
        setStatus(bill.activationStatus ?? '');
        setCadence(bill.cadence ?? '');
    };

    const [name, setName] = useState(
        bill.displayName ?? bill.name ?? bill.payee ?? '',
    );
    const [amount, setAmount] = useState(centsToDollars(bill.amount) ?? 0);
    const [category, setCategory] = useState(bill.category ?? '');
    const [cadence, setCadence] = useState(bill.cadence ?? '');
    const [status, setStatus] = useState(bill.activationStatus ?? '');
    const [showSpinner, setShowSpinner] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{
        name?: string;
        amount?: string;
    }>({});

    useEffect(() => {
        resetForm();
    }, [isOpen]);
    const toast = useToastController();
    const { upsertBillAsync } = useUpsertBillAndRefetch(bill.membershipId);
    const handleSave = async () => {
        const errors: typeof validationErrors = {};
        if (!name.trim()) errors.name = 'Name is required.';
        if (amount <= 0) errors.amount = 'Amount must be greater than 0.';

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        setShowSpinner(true);

        try {
            const upsertBillBody = {
                id: bill.id,
                membershipId: bill.membershipId,
                displayName: name,
                amount: dollarsToCents(amount),
                accountId: bill.accountId,
                category,
                activationStatus: status,
                cadence,
                payee: bill.payee,
            };

            await upsertBillAsync(upsertBillBody);

            onClose();

            toast.show('Bill updated', MutationSuccessToastOptions);
        } catch (error) {
            toast.show('Error updating bill', MutationErrorToastOptions());
        } finally {
            setShowSpinner(false);
        }
    };

    return (
        <div style={{ width: 500 }}>
            <GroveSimpleModal isOpen={isOpen} onClose={onClose} hasCloseButton>
                <YStack gap={16} padding={16} width={600}>
                    <BodyLargeEmphasized>Edit Bill</BodyLargeEmphasized>
                    <YStack>
                        <BodyMediumRegular>Name</BodyMediumRegular>
                        <InputText
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value ?? '');
                                validationErrors.name = undefined;
                            }}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                            }}
                            disabled={showSpinner}
                        />
                        {validationErrors.name !== null && (
                            <BodyMediumRegular color="red">
                                {validationErrors.name}
                            </BodyMediumRegular>
                        )}
                    </YStack>

                    <YStack>
                        <Label htmlFor="category">Category</Label>
                        <select
                            id="category"
                            value={category}
                            onChange={(e) => {
                                setCategory(e.target.value);
                            }}
                            disabled={showSpinner}
                            style={{
                                padding: 12,
                                borderRadius: 8,
                                borderWidth: 1,
                                borderColor: '#ccc',
                                fontSize: 16,
                            }}
                        >
                            {Object.values(BillCategory).map((value) => (
                                <option key={value} value={value}>
                                    {value
                                        .replace(/_/g, ' ')
                                        .replace(/\b\w/g, (l) =>
                                            l.toUpperCase(),
                                        )}
                                </option>
                            ))}
                        </select>
                    </YStack>

                    <YStack>
                        <Label htmlFor="status">Status</Label>
                        <select
                            id="status"
                            value={status}
                            onChange={(e) => {
                                setStatus(
                                    e.target.value as BillActivationStatus,
                                );
                            }}
                            disabled={showSpinner}
                            style={{
                                padding: 12,
                                borderRadius: 8,
                                borderWidth: 1,
                                borderColor: '#ccc',
                                fontSize: 16,
                            }}
                        >
                            <option value="ACTIVE">Active</option>
                            <option value="REVIEW">Review</option>
                            <option value="REJECTED">Rejected</option>
                        </select>
                    </YStack>

                    <YStack>
                        <Label htmlFor="cadence">Cadence</Label>
                        <select
                            id="cadence"
                            value={cadence}
                            onChange={(e) => {
                                setCadence(e.target.value as BillingCadence);
                            }}
                            disabled={showSpinner}
                            style={{
                                padding: 12,
                                borderRadius: 8,
                                borderWidth: 1,
                                borderColor: '#ccc',
                                fontSize: 16,
                            }}
                        >
                            {Object.values(BillingCadence)
                                .filter(
                                    (value) => value !== BillingCadence.YEARLY,
                                )
                                .map((value) => (
                                    <option key={value} value={value}>
                                        {value
                                            .replace(/_/g, '-')
                                            .toLowerCase()
                                            .replace(/\b\w/g, (c) =>
                                                c.toUpperCase(),
                                            )}
                                    </option>
                                ))}
                        </select>
                    </YStack>

                    <YStack>
                        <BodyMediumRegular>Amount</BodyMediumRegular>
                        <InputNumber
                            disabled={showSpinner}
                            value={amount}
                            onValueChange={(e) => {
                                setAmount(e.value ?? 0);
                                validationErrors.amount = undefined;
                            }}
                            mode="currency"
                            currency="USD"
                            min={0}
                        />
                        {validationErrors.amount !== null && (
                            <BodyMediumRegular color="red">
                                {validationErrors.amount}
                            </BodyMediumRegular>
                        )}
                    </YStack>

                    {showSpinner && (
                        <XStack gap={12}>
                            <Spinner />
                        </XStack>
                    )}
                    {!showSpinner && (
                        <XStack gap={12}>
                            <Button
                                flex={1}
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.SMALL}
                                tone="neutral"
                                text="Cancel"
                                onPress={onClose}
                            />
                            <Button
                                flex={1}
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.SMALL}
                                tone="positive"
                                text="Save"
                                onPress={handleSave}
                            />
                        </XStack>
                    )}
                </YStack>
            </GroveSimpleModal>
        </div>
    );
};
